/**
 * useCSVExport Hook
 *
 * Reusable custom hook for CSV export functionality.
 * Eliminates code duplication between ExportCSVButton and ExportCalculationSection.
 *
 * @version 1.0.0
 * @created 2025-10-28
 */

import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX, IconAlertTriangle } from '@tabler/icons-react';
import { generateCalculationCSV, downloadCalculationCSV } from '../utils/csv';

/**
 * Rate Limiting Configuration
 */
const RATE_LIMIT_KEY = 'csv_export_timestamps';
const RATE_LIMIT_MAX_EXPORTS = 5;
const RATE_LIMIT_WINDOW_MS = 60000; // 1 minute

/**
 * Calculation Data Input
 */
interface CalculationData {
  year: number;
  monthlyConsumption: number[];
  hourlyConfig: {
    weekday: number[];
    weekend: number[];
  };
}

/**
 * Hook Options
 */
interface UseCSVExportOptions {
  /** Calculation data for CSV generation */
  calculationData: CalculationData;

  /** Location for GA4 tracking (e.g., 'calculator', 'dashboard_quick_actions', 'dashboard_card') */
  location?: string;
}

/**
 * Hook Return Value
 */
interface UseCSVExportReturn {
  /** Loading state during CSV generation and download */
  loading: boolean;

  /** Function to trigger CSV export */
  handleExport: () => Promise<void>;
}

/**
 * Custom hook for CSV export functionality
 *
 * Handles:
 * - Loading state management
 * - CSV generation and download
 * - GA4 analytics tracking
 * - Success/error notifications
 * - Error handling with development logging
 *
 * @param options - Hook options
 * @returns Loading state and export handler
 *
 * @example
 * ```tsx
 * const { loading, handleExport } = useCSVExport({
 *   calculationData: {
 *     year: 2025,
 *     monthlyConsumption: [100, 120, ...],
 *     hourlyConfig: { weekday: [...], weekend: [...] }
 *   },
 *   location: 'calculator'
 * });
 *
 * <Button onClick={handleExport} loading={loading}>
 *   Export CSV
 * </Button>
 * ```
 */
/**
 * Check if rate limit is exceeded
 * @returns true if rate limit exceeded, false otherwise
 */
function checkRateLimit(): boolean {
  try {
    const now = Date.now();
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const timestamps: number[] = stored ? JSON.parse(stored) : [];

    // Filter out timestamps older than the rate limit window
    const recentTimestamps = timestamps.filter(
      (timestamp) => now - timestamp < RATE_LIMIT_WINDOW_MS
    );

    // Check if rate limit exceeded
    return recentTimestamps.length >= RATE_LIMIT_MAX_EXPORTS;
  } catch (error) {
    // If localStorage fails, allow the export (fail open)
    if (import.meta.env.DEV) {
      console.error('[useCSVExport] checkRateLimit localStorage error:', error);
    }
    return false;
  }
}

/**
 * Record export attempt timestamp
 */
function recordExportAttempt(): void {
  try {
    const now = Date.now();
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const timestamps: number[] = stored ? JSON.parse(stored) : [];

    // Add current timestamp
    timestamps.push(now);

    // Filter out old timestamps (cleanup)
    const recentTimestamps = timestamps.filter(
      (timestamp) => now - timestamp < RATE_LIMIT_WINDOW_MS
    );

    // Store updated timestamps
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentTimestamps));
  } catch (error) {
    // If localStorage fails, continue (fail open)
    if (import.meta.env.DEV) {
      console.error('[useCSVExport] recordExportAttempt localStorage error:', error);
    }
  }
}

export function useCSVExport({
  calculationData,
  location = 'calculator',
}: UseCSVExportOptions): UseCSVExportReturn {
  const [loading, setLoading] = useState(false);

  /**
   * Handle CSV export with analytics and notifications
   */
  const handleExport = async () => {
    try {
      // Check rate limit BEFORE setting loading state
      if (checkRateLimit()) {
        notifications.show({
          title: 'Zbyt wiele eksportów',
          message: `Możesz wyeksportować maksymalnie ${RATE_LIMIT_MAX_EXPORTS} plików na minutę. Spróbuj ponownie za chwilę.`,
          color: 'orange',
          icon: <IconAlertTriangle size={18} />,
          autoClose: 5000,
        });

        // GA4: Track rate limit hit
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'csv_export_rate_limit', {
            location,
            year: calculationData.year,
          });
        }

        return; // Exit early - don't proceed with export
      }

      setLoading(true);

      // Record this export attempt
      recordExportAttempt();

      // GA4: Track export button click
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'export_calculation_csv', {
          location,
          year: calculationData.year,
        });
      }

      // Generate CSV
      const csv = generateCalculationCSV(calculationData);

      // Download CSV
      downloadCalculationCSV(csv, calculationData.year);

      // GA4: Track successful download
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'csv_download_success', {
          file_size: csv.length,
          year: calculationData.year,
        });
      }

      // Show success notification
      notifications.show({
        title: 'Sukces',
        message: 'Plik CSV został pobrany',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    } catch (error) {
      // GA4: Track download error
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'csv_download_error', {
          error_type: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      // Show error notification
      notifications.show({
        title: 'Błąd',
        message: 'Nie udało się wygenerować pliku CSV',
        color: 'red',
        icon: <IconX size={18} />,
      });

      // Development only logging
      if (import.meta.env.DEV) {
        console.error('[useCSVExport] handleExport error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleExport,
  };
}
